import React from 'react';
import {
  Truck,
  Activity,
  X,
  Check,
  Clock,
  MapPin,
  Package,
} from 'lucide-react';
import { Order } from '../../../../../../types/Order.types';
import L from 'leaflet';
import { Map, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import './ShipmentTrackingModal.css';

const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41]
});

export const ShipmentTrackingModal = ({ order, onClose }: { order: Order, onClose: () => void }) => {
  if (!order.tracking) return null;

  return (
    <div
      className="shipment-tracking-modal-overlay"
      onClick={onClose}
    >
      <div
        className="shipment-tracking-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left Side: Info & Timeline */}
        <div className="shipment-tracking-modal-left">
          <div className="shipment-tracking-modal-header">
            <div className="shipment-tracking-modal-header-info">
              <div className="shipment-tracking-modal-icon-wrapper">
                <Package size={20} />
              </div>
              <div>
                <h3 className="shipment-tracking-modal-order-id">{order.id}</h3>
                <p className="shipment-tracking-modal-tracking-number">Tracking: {order.tracking.number}</p>
              </div>
            </div>
            <button onClick={onClose} className="shipment-tracking-modal-close-mobile">
              <X size={20} />
            </button>
          </div>

          <div className="shipment-tracking-modal-details">
            <div className="shipment-tracking-modal-company-info">
              <div className="shipment-tracking-modal-company-text">
                <span className="shipment-tracking-modal-company-label">Compañía_Transporte</span>
                <span className="shipment-tracking-modal-company-name">{order.tracking.company}</span>
              </div>
              <Truck size={16} className="shipment-tracking-modal-company-icon" />
            </div>

            <div className="shipment-tracking-modal-timeline-container">
              <h4 className="shipment-tracking-modal-timeline-title">
                <Activity size={10} />
                Línea_De_Tiempo_Envío
              </h4>
              <div className="shipment-tracking-modal-timeline">
                {order.tracking.timeline.map((step, idx) => (
                  <div key={idx} className="shipment-tracking-modal-timeline-item">
                    <div className="shipment-tracking-modal-timeline-dot" data-completed={step.completed}>
                      {step.completed ? <Check size={8} className="shipment-tracking-modal-check-icon" /> : <div className="shipment-tracking-modal-empty-dot" />}
                    </div>
                    <div className="shipment-tracking-modal-timeline-content">
                      <span className="shipment-tracking-modal-timeline-stage" data-completed={step.completed}>{step.stage}</span>
                      <div className="shipment-tracking-modal-timeline-meta">
                        <Clock size={8} />
                        <span>{new Date(step.date).toLocaleString()}</span>
                        {step.location && (
                          <>
                            <span className="shipment-tracking-modal-timeline-separator">|</span>
                            <MapPin size={8} />
                            <span>{step.location}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Map & Actions */}
        <div className="shipment-tracking-modal-right">
          <div className="shipment-tracking-modal-map-container">
            <Map
              center={order.tracking.timeline.find(s => !s.completed)?.coordinates || order.tracking.timeline[order.tracking.timeline.length - 1].coordinates || [40.4168, -3.7038]}
              zoom={6}
              style={{ height: '100%', width: '100%', filter: 'invert(100%) hue-rotate(180deg) brightness(0.8) contrast(1.2)' }}
              zoomControl={false}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              {order.tracking.timeline.map((step, idx) => step.coordinates && (
                <Marker key={idx} position={step.coordinates} icon={defaultIcon}>
                  <Popup>
                    <div className="shipment-tracking-modal-map-popup">
                      <p className="shipment-tracking-modal-map-popup-title">{step.stage}</p>
                      <p className="shipment-tracking-modal-map-popup-loc">{step.location}</p>
                    </div>
                  </Popup>
                </Marker>
              ))}
              <Polyline
                positions={order.tracking.timeline.map(s => s.coordinates).filter((c): c is [number, number] => !!c)}
                color="var(--color-brand-green)"
                weight={2}
                dashArray="5, 5"
              />
            </Map>

            <div className="shipment-tracking-modal-gps-status">
              <div className="shipment-tracking-modal-gps-dot" />
              <span className="shipment-tracking-modal-gps-text">Live_GPS_Signal: Active</span>
            </div>
          </div>
          <div className="shipment-tracking-modal-actions">
            <div className="shipment-tracking-modal-status-container">
              <span className="shipment-tracking-modal-status-label">Estado_Actual</span>
              <div className="shipment-tracking-modal-status-value">
                {order.status}
              </div>
            </div>
            <button className="shipment-tracking-modal-btn-contact">
              Contactar_Transportista
            </button>
            <button
              onClick={onClose}
              className="shipment-tracking-modal-btn-close"
            >
              Cerrar_Ventana
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
